import { uniq, uniqBy, isEqual } from 'lodash';
import { createSelector } from 'reselect';
import moment from 'moment';
import { openDutyTransactionsByMembershipId, augmentedDutyRequests } from '../duty-transactions/selectors';
import { overlappingIntervals, isPeriodFullyCovered, anyOverlappingIntervals } from '../../../utils/schedules';
import { cacheArray } from '../../../utils/memoize';
import { openStates } from '../../../utils/dutyTransactions';
import { orderBy } from 'lodash';

const defaultFunctionId = state => state.dutyPlanner.defaultFunctionId;
const groupId = state => state.dutyPlanner.groupId;
const startTime = state => state.dutyPlanner.startTime;
const endTime = state => state.dutyPlanner.endTime;
const membershipsById = state => state.memberships.byId;
const usersById = state => state.users.byId;
const skillsById = state => state.skills.byId;
const functionsById = state => state.standbyDutyFunctions.byId;
const groupsById = state => state.groups.byId;
const availabilityCodesById = state => state.availabilityCodes.byId;
const membershipCombinedSchedules = state => state.combinedSchedules.full.membership;
const readOnly = state => state.dutyPlanner.readOnly;

export const defaultFunction = createSelector(
  defaultFunctionId,
  functionsById,
  (defaultFunctionId, functionsById) => functionsById[defaultFunctionId] || null
);

// Returns a list of stations that are descendants of the group, including the
// group itself if it is a station. This is used to determine which stations to
// display in the duty planner, since the duty functions are assigned to
// stations. If the group is a cluster, it will return all the stations that are
// part of the cluster. If the group is a station, it will return just that
// station.
export const dutyPlannerStations = createSelector(groupId, groupsById, (groupId, groupsById) => {
  const group = groupsById[groupId];
  if (!group) return [];
  const descendants = (group.descendant_ids || []).map(id => groupsById[id]);
  return [...descendants, group].filter(Boolean).filter(g => g.type === 'station');
});

const groupMemberships = createSelector(dutyPlannerStations, membershipsById, (stations, membershipsById) => {
  const stationIds = new Set(stations.map(s => s.id));
  return uniq(Object.values(membershipsById).filter(m => m.duty && stationIds.has(m.group_id)));
});

// Returns an array of objects, each containing a station and the list of active
// duty functions that are assigned to that station. The functions are ordered
// by their display order. This is used to display the functions in the duty
// planner, since the planner is organized by station, and for each station, the
// functions are displayed in a specific order.
export const dutyFunctionsPerStation = createSelector(
  dutyPlannerStations,
  functionsById,
  readOnly,
  (stations, functionsById, readOnly) => {
    const functionsPerStation = [];
    const allFunctions = Object.values(functionsById).filter(f => f.active);

    for (const station of stations) {
      const functions = (
        readOnly ? allFunctions : allFunctions.filter(f => f.id !== station.default_duty_planner_duty_function_id)
      ).map(f => ({ ...f, title: f.name }));

      functionsPerStation.push({
        station,
        functions: orderBy(
          functions.filter(func => func.group_ids.includes(station.id)),
          'display_order'
        )
      });
    }

    return orderBy(functionsPerStation, 'station.name');
  }
);

// Returns a list of users, augmented with their skills, the id of the specific membership
// for the group, and the schedule intervals that contain the performed duty function
const allUsersAugmented = createSelector(
  usersById,
  groupMemberships,
  skillsById,
  openDutyTransactionsByMembershipId,
  availabilityCodesById,
  membershipCombinedSchedules,
  startTime,
  endTime,
  (usersById, memberships, skillsById, dutyTransactions, availabilityCodesById, schedules, startTime, endTime) =>
    memberships
      .filter(m => m.duty === true)
      .map(membership => ({
        membership,
        user: usersById[membership.user_id],
        schedule: schedules[membership.id]
      }))
      // filter out the memberships which don't have an associated user and
      // schedule
      .filter(({ user, schedule }) => user && schedule)
      .map(({ membership, user, schedule }) => ({
        // add info about membership and skills
        user: {
          ...user,
          membershipId: membership.id,
          stationId: membership.group_id,
          displayOrder: membership.display_order,
          skills: (membership.skills || []).map(id => skillsById[id])
        },
        schedule
      }))
      .map(({ user, schedule }) => ({
        // This filters the duty transactions that are only contained in the viewing period.
        user: {
          ...user,
          dutyTransactions: overlappingIntervals(dutyTransactions[user.membershipId], startTime, endTime).map(dt => ({
            ...dt,
            availabilityCode: availabilityCodesById[dt.availability_code_id]
          }))
        },
        schedule
      }))
      .map(({ user, schedule }) => ({
        // get the intervals in the current period
        user,
        intervalsInPeriod: schedule.intervals.filter(
          i => i.active && moment(i.start_time).isBefore(endTime) && moment(i.end_time).isAfter(startTime)
        )
      }))
      .flatMap(({ user, intervalsInPeriod }) =>
        augmentIntervalInformation(user, intervalsInPeriod, availabilityCodesById, startTime, endTime)
      )
);

const augmentIntervalInformationCache = {};

const augmentIntervalInformation = (user, intervalsInPeriod, availabilityCodes, startTime, endTime) => {
  const membershipAssignments = [];

  for (let interval of intervalsInPeriod) {
    const availabilityCode = availabilityCodes[interval.availability_code_id] || null;
    const minEndTime = moment.min(moment(interval.end_time), moment(endTime));
    const maxStartTime = moment.max(moment(interval.start_time), moment(startTime));
    const intervalLength = interval.available ? minEndTime.diff(maxStartTime, 'seconds') : 0;

    interval = {
      ...interval,
      availabilityCode,
      start_time: maxStartTime.valueOf(),
      end_time: minEndTime.valueOf()
    };

    if (interval.assigned_function_ids.length === 0) {
      membershipAssignments.push({
        ...user,
        functionId: null,
        intervals: [interval]
      });
    }

    interval.assigned_function_ids.map(functionId => {
      let existingAssignment = membershipAssignments.find(assignment => assignment.functionId === functionId);

      if (!existingAssignment) {
        existingAssignment = {
          ...user,
          functionId,
          intervals: [],
          availabilityLength: 0
        };
        membershipAssignments.push(existingAssignment);
      }

      existingAssignment.availabilityLength += intervalLength;
      existingAssignment.intervals.push(interval);
    });
  }

  // if the computed value has exactly the same value as the cache, return the
  // cached value. This is to avoid rerenders by ensuring the objects stay the
  // same
  if (isEqual(augmentIntervalInformationCache[user.membershipId], membershipAssignments)) {
    return augmentIntervalInformationCache[user.membershipId];
  }

  return (augmentIntervalInformationCache[user.membershipId] = membershipAssignments);
};

// Filters from all the augmented users those who have a function assigned and
// is exactly the default one
export const candidateUsers = createSelector(
  allUsersAugmented,
  defaultFunctionId,
  (allUsersAugmented, defaultFunctionId) =>
    cacheArray(
      'candidateUsers',
      allUsersAugmented.filter(i => i.functionId === defaultFunctionId)
    )
);

export const usersPerStationCategorized = createSelector(
  allUsersAugmented,
  dutyPlannerStations,
  (allUsersAugmented, dutyPlannerStations) => {
    const result = {};

    for (const station of dutyPlannerStations) {
      const defaultFunctionId = station.default_duty_planner_duty_function_id;

      const stationUsers = allUsersAugmented.filter(user => user.stationId === station.id);

      const onDefaultDuty = stationUsers.filter(user => user.functionId === defaultFunctionId);
      const ids = uniq(onDefaultDuty.map(user => user.id));
      const offDuty = uniqBy(
        stationUsers.filter(user => !user.functionId && !ids.includes(user.id)),
        'id'
      );
      const onCall = uniqBy(
        stationUsers.filter(u => u.intervals.find(i => i.available && i.assigned_function_ids.length === 0)),
        'id'
      );

      result[station.id] = {
        onDefaultDuty,
        offDuty,
        onCall
      };
    }
    return result;
  }
);

// Filters from all the augmented users those who are available on call
export const usersOnCall = createSelector(allUsersAugmented, allUsersAugmented =>
  cacheArray(
    'usersOnCall',
    uniqBy(
      allUsersAugmented.filter(u => u.intervals.find(i => i.available && i.assigned_function_ids.length === 0)),
      'id'
    )
  )
);

// Filters from all the augmented users those who have no functions assigned
export const usersOffDuty = createSelector(allUsersAugmented, candidateUsers, (allUsersAugmented, candidateUsers) => {
  const ids = uniq(candidateUsers.map(i => i.id));
  // since we have an entry of one user in each of their intervals from the
  // all users augmented selector and here, we only care about
  // displaying the user, just display them once.
  return cacheArray(
    'usersOffDuty',
    uniqBy(
      allUsersAugmented.filter(u => !u.functionId && !ids.includes(u.id)),
      'id'
    )
  );
});

// Filters from all the augmented users the ones with a function assigned, but
// not the default one
const assignedUsers = createSelector(allUsersAugmented, defaultFunctionId, (allUsersAugmented, defaultFunctionId) =>
  cacheArray(
    'assignedUsers',
    allUsersAugmented.filter(u => u.functionId && u.functionId != defaultFunctionId)
  )
);

// Used by the timeline component. These are the items in the left side of the
// timeline, which represent the different functions that are being planned
const timelineGroups = createSelector(dutyFunctionsPerStation, dutyFunctionsPerStation => {
  const result = [];
  const stationCount = Object.keys(dutyFunctionsPerStation).length;
  for (const { station, functions } of dutyFunctionsPerStation) {
    // only show the separator if there are more than 1 station, and the station
    // has functions
    if (stationCount > 1 && functions.length > 0) {
      result.push({
        type: 'station-separator',
        id: station.id,
        title: station.name,
        skill_id: -999
      });
    }

    for (const func of functions) {
      result.push({
        ...func,
        id: `${station.id}_${func.id}`,
        functionId: func.id,
        stationId: station.id,
        title: func.name
      });
    }
  }
  return cacheArray('timelineGroups', result);
});

// This selector is only used by the timeline component. This will be left here
// for now.
export const assignedUsersTimelineItems = createSelector(
  assignedUsers,
  timelineGroups,
  (assignedUsers, timelineGroups) => {
    // Ensure that the items are generated using the correct locale. The language
    // of the Timeline Component depends on it
    moment.locale((globalThis.I18n || {}).locale);
    const items = [];

    // NOTE: assignedUsers is already split by assigned function. If there is an
    // interval with 2 functions assigned, there will be 2 entries
    for (const user of assignedUsers) {
      for (const interval of user.intervals) {
        // we filter the duty Transactions that apply within the interval period,
        // these will be used to display the transactions in the timeline
        const dutyTransactions = overlappingIntervals(user.dutyTransactions, interval.start_time, interval.end_time);
        const start_time = moment(interval.start_time);
        const end_time = moment(interval.end_time);

        // this is the row in the timeline that the user will be displayed.
        const intervalStationId = interval.station_id || user.stationId;
        const group = timelineGroups.find(g => g.functionId == user.functionId && g.stationId == intervalStationId);
        const item = {
          ...interval,
          id: `${user.membershipId}+${user.functionId}-${start_time.valueOf()}-${end_time.valueOf()}`,
          membership_id: user.membershipId,
          original_station_id: user.stationId, // in case user is moved
          group: group?.id || null,
          dutyTransactions,
          userNickname: `${user.nickname}`,
          start_time,
          end_time
        };
        items.push(item);
      }
    }
    return cacheArray('assignedUsersTimelineItems', items);
  }
);

export const timelineGroupsAugmented = createSelector(
  timelineGroups,
  assignedUsersTimelineItems,
  startTime,
  endTime,
  skillsById,
  (timelineGroups, assignedUsersTimelineItems, startTime, endTime, skillsById) =>
    timelineGroups.map(group => {
      const intervals = assignedUsersTimelineItems.filter(
        i => i.group == group.id && (!i.availabilityCode || i.availabilityCode.available_for_crewing)
      );

      // True if the duty function has someone assigned for the entire
      // current viewing period
      const isCovered = isPeriodFullyCovered(intervals, startTime, endTime);
      // True if the duty function has more than one person covering it
      // for the same period during the viewing period
      const isOverCovered = anyOverlappingIntervals(intervals);

      return {
        ...group,
        skill: skillsById[group.skill_id],
        isCovered,
        isOverCovered
      };
    })
);

export const dutyRequestTimelineItems = createSelector(
  augmentedDutyRequests,
  startTime,
  endTime,
  (dutyRequests, startTime, endTime) => {
    // Ensure that the items are generated using the correct locale. The language
    // of the Timeline Component depends on it
    moment.locale((globalThis.I18n || {}).locale);

    const dutyRequestsForViewingPeriod = overlappingIntervals(
      dutyRequests.filter(r => openStates.includes(r.state)),
      startTime,
      endTime
    );

    const items = dutyRequestsForViewingPeriod.map(dr => {
      const start_time = moment(dr.start_time);
      const end_time = moment(dr.end_time);

      return {
        ...dr,
        id: `duty_request_${dr.id}+${start_time.valueOf()}-${end_time.valueOf()}`,
        dutyTransactionType: 'duty_request',
        group: dr.requested_duty_function_ids[0],
        assigned_function_ids: dr.requested_duty_function_ids,
        dutyTransactions: [dr],
        start_time,
        end_time
      };
    });
    return cacheArray('dutyRequestTimelineItems', items);
  }
);

export const stationSettings = createSelector(
  dutyPlannerStations,
  functionsById,
  dutyFunctionsPerStation,
  usersPerStationCategorized,
  (stations, functionsById, dutyFunctionsPerStation, candidatesPerStation) => {
    const settings = {};
    for (const station of stations) {
      const defaultFunctionId = station.default_duty_planner_duty_function_id;
      const defaultFunction = defaultFunctionId ? functionsById[defaultFunctionId] : null;
      const usersCategorized = candidatesPerStation[station.id] || [];
      const stationFunctions = dutyFunctionsPerStation.find(df => df.station?.id === station.id);

      settings[station.id] = {
        defaultFunctionId,
        defaultFunction,
        candidateUsers: usersCategorized.onDefaultDuty || [],
        usersOnCall: usersCategorized.onCall || [],
        usersOffDuty: usersCategorized.offDuty || [],
        functions: stationFunctions?.functions || [],
        allowMultipleFunctions: station.allow_multiple_duty_functions_per_interval === true
      };
    }

    return settings;
  }
);
